import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { shuffle } from 'shuffle-seed';
import { useSelector } from 'react-redux';

import ReactSelect from 'react-select';
import AsyncReactSelect from 'react-select/async';
import CreatableReactSelect from 'react-select/creatable';
import AsyncCreatableReactSelect from 'react-select/async-creatable';

import Icon from '../../Icons/Icon';
import Typography from '../../Typography/Typography';
import Box from '../../Styled/Box';
import Tooltip from '../../Tooltips/Tooltip';

import { useTheme } from '../../../hooks';
import { userSelectors } from '../../../redux/selectors';

import selectStyles from './selectStyles';

// em ordem de prioridade
const getVariant = ({ error, disabled, variant }) => {
  if (disabled) {
    return 'disabled';
  }

  if (error) {
    return 'error';
  }

  if (variant) {
    return variant;
  }

  return 'primary';
};

const LoadingIndicator = () => (
  <Icon
    spin
    fixedWidth
    icon='spinner'
    mr='16px'
  />
);

const TooltipLabel = ({
  message,
  id,
  icon: iconProps,
  tooltip: tooltipProps,
}) => (
  <>
    {' '}
    <Icon
      style={{ cursor: 'pointer' }}
      color='coldgrey400'
      icon='question-circle'
      data-tip
      data-for={`tooltip-${id}`}
      {...iconProps}
    />

    <Tooltip
      id={`tooltip-${id}`}
      getContent={() => message}
      {...tooltipProps}
    />
  </>
);

TooltipLabel.propTypes = {
  message: PropTypes.node.isRequired,
  id: PropTypes.string.isRequired,
  icon: PropTypes.object,
  tooltip: PropTypes.object,
};

TooltipLabel.defaultProps = {
  icon: {},
  tooltip: {},
};

const getOptionValue = (option) => option.id;

const getOptionLabel = (option) => option.name;

const ascendingSort = (a, b, locale) => a.text.localeCompare(b.text, locale, { numeric: true });
const descendingSort = (a, b, locale) => b.text.localeCompare(a.text, locale, { numeric: true });

const sortOptions = (type, options, language, seed) => {
  switch (type) {
  case 'shuffle':
    return shuffle(options, seed);

  case 'ascending':
    return options.sort((a, b) => ascendingSort(a, b, language));

  case 'descending':
    return options.sort((a, b) => descendingSort(a, b, language));

  default:
    return options;
  }
};

const Select = (props) => {
  const {
    creatable,
    async,
    size,
    onChange,
    value,
    error,
    disabled,
    label,
    helperText,
    boxProps,
    labelProps,
    name,
    helperTextProps,
    styles,
    options,
    required,
    tooltip,
    tooltipProps,
    components,
    sortType,
    seed,
    ...inputProps
  } = props;
  const theme = useTheme();
  const activeLanguage = useSelector(userSelectors.activeLanguage);

  const variant = getVariant(props);

  const defaultStyleProps = theme.inputs.variants.default;
  const sizeProps = theme.inputs.size[size] || theme.inputs.size.md;
  const variantProps = theme.inputs.variants[variant] || theme.inputs.variants.primary;

  const SelectComponent = useMemo(() => {
    if (creatable && !async) { return CreatableReactSelect; }
    if (creatable && async) { return AsyncCreatableReactSelect; }
    if (async && !creatable) { return AsyncReactSelect; }
    return ReactSelect;
  }, [creatable, async]);

  const selectOptions = React.useMemo(
    () => sortOptions(sortType, options, activeLanguage, seed),
    [activeLanguage, options, seed, sortType],
  );

  const _onChange = React.useCallback((val) => {
    // @guileite Necessário quando creatable for false
    if (val?.onClick && !creatable) { val.onClick(val); }

    onChange(val);
  }, [onChange, creatable]);

  return (
    <Box width='100%' {...boxProps}>
      {label && (
        <Typography
          display='block'
          mb={2}
          htmlFor={name}
          className='force-inline-children'
          {...(sizeProps.label || {})}
          {...(defaultStyleProps.label || {})}
          {...(variantProps.label || {})}
          {...labelProps}
        >
          {label}
          {Boolean(required) && ' *'}
          {tooltip && (<TooltipLabel id={name} message={tooltip} {...tooltipProps} />)}
        </Typography>
      )}

      <SelectComponent
        isDisabled={disabled}
        options={selectOptions}
        value={value}
        onChange={_onChange}
        name={name}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        menuPlacement='auto'
        maxMenuHeight={320}
        components={{
          LoadingIndicator,
          ...components,
        }}
        isSearchable={false}
        {...inputProps}
        styles={selectStyles({
          props,
          theme,
          sizeProps,
          variantProps,
          styleProps: defaultStyleProps,
          styles,
        })}
      />

      {(error || helperText) && (
        <Typography
          display='block'
          mt={1}
          variant='helper'
          color={error ? 'danger' : 'coldgrey600'}
          {...helperTextProps}
        >
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

Select.propTypes = {
  size: PropTypes.string,
  tag: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  value: PropTypes.any,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onChange: PropTypes.func,
  label: PropTypes.node,
  helperText: PropTypes.node,
  boxProps: PropTypes.object,
  labelProps: PropTypes.object,
  iconProps: PropTypes.object,
  textarea: PropTypes.bool,
  name: PropTypes.string,
  helperTextProps: PropTypes.object,
  styles: PropTypes.object,
  creatable: PropTypes.bool,
  async: PropTypes.bool,
  options: PropTypes.array.isRequired,
  required: PropTypes.bool,
  tooltip: PropTypes.node,
  tooltipProps: PropTypes.object,
  components: PropTypes.object,
  sortType: PropTypes.string,
  seed: PropTypes.number.isRequired,
};

Select.defaultProps = {
  textarea: false,
  label: '',
  size: 'md',
  tag: 'body',
  error: null,
  disabled: false,
  icon: null,
  value: '',
  onChange: () => { },
  helperText: null,
  boxProps: {},
  labelProps: {},
  iconProps: {},
  name: '',
  helperTextProps: {},
  styles: {},
  creatable: false,
  async: false,
  options: null,
  required: false,
  tooltip: '',
  tooltipProps: {},
  components: null,
  sortType: 'none',
  seed: 0,
};

export default Select;
